import { useState, useMemo, useEffect, useCallback } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import api from '../../services/api';
import useAppStore from '../../store/app.store';

const moeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (gasto, total) => total > 0 ? Math.min(100, Math.round((gasto / total) * 100)) : 0;
const hoje = () => new Date().toISOString().split('T')[0];

const Ico = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
  </svg>
);

const STATUS_COR = { planejamento: '#94A3B8', ativa: '#4ADE80', pausada: '#FBBF24', encerrada: '#F87171' };
const STATUS_BG  = { planejamento: 'rgba(148,163,184,.12)', ativa: 'rgba(74,222,128,.12)', pausada: 'rgba(251,191,36,.12)', encerrada: 'rgba(248,113,113,.12)' };
const PLAT_LABEL = { ambos: 'G + M', google: 'Google', meta: 'Meta' };
const PLAT_COR   = { ambos: '#A78BFA', google: '#4285F4', meta: '#1877F2' };
const PLAT_BG    = { ambos: 'rgba(167,139,250,.12)', google: 'rgba(66,133,244,.12)', meta: 'rgba(24,119,242,.12)' };
const TIPO_LABEL = { criativo: 'Criativo', copy: 'Copy', roteiro: 'Roteiro', captacao: 'Captação', edicao: 'Edição' };
const TIPO_COR   = { criativo: '#A78BFA', copy: '#38BDF8', roteiro: '#FBBF24', captacao: '#F472B6', edicao: '#4ADE80' };
const DEM_ST_COR = { pendente: '#FBBF24', em_andamento: '#38BDF8', entregue: '#A78BFA', aprovado: '#4ADE80', cancelado: '#F87171' };
const REUN_TIPO_LABEL = { alinhamento: 'Alinhamento', planejamento: 'Planejamento', resultados: 'Resultados', outros: 'Outros' };
const REUN_TIPO_COR   = { alinhamento: '#38BDF8', planejamento: '#A78BFA', resultados: '#4ADE80', outros: '#94A3B8' };
const REUN_ST_COR     = { agendada: '#FBBF24', realizada: '#4ADE80', cancelada: '#F87171', reagendada: '#94A3B8' };

function Tabs({ aba, setAba, tabs }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #2A3652', overflowX: 'auto' }}>
      {tabs.map(t => (
        <button key={t.k} onClick={() => setAba(t.k)} style={{
          display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
          padding: '8px 18px', fontSize: 14,
          fontWeight: aba === t.k ? 600 : 400,
          color: aba === t.k ? '#6C4CF5' : '#94A3B8',
          background: 'none', border: 'none',
          borderBottom: aba === t.k ? '2px solid #6C4CF5' : '2px solid transparent',
          cursor: 'pointer', transition: 'all .15s', marginBottom: -1,
        }}>
          {t.icon}{t.l}
        </button>
      ))}
    </div>
  );
}

function VerbaBar({ gasto, total, label, cor }) {
  const p = pct(gasto, total);
  const barCor = p >= 90 ? '#F87171' : p >= 75 ? '#FBBF24' : '#4ADE80';
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: cor, fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 11, color: p >= 75 ? barCor : '#94A3B8' }}>{moeda(gasto)} / {moeda(total)} ({p}%)</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,.06)' }}>
        <div style={{ height: '100%', borderRadius: 999, background: barCor, width: `${p}%`, transition: 'width .4s' }} />
      </div>
    </div>
  );
}

// ── FORMULÁRIOS ───────────────────────────────────────────────────────────────

function FormCampanha({ inicial, clientes, onSalvar, onFechar }) {
  const [f, setF] = useState(inicial || {
    cliente_id: '', nome: '', plataforma: 'ambos', tipo: 'permanente',
    objetivo: '', verba_total: '', data_inicio: '', data_fim: '', descricao: '', status: 'planejamento',
  });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(f); }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="form-label">Cliente *</label>
          <select className="form-input" value={f.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
            <option value="">Selecione...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Nome da campanha *</label>
          <input className="form-input" value={f.nome} onChange={e => s('nome', e.target.value)} required placeholder="Ex: Black Friday 2026" />
        </div>
        <div>
          <label className="form-label">Plataforma</label>
          <select className="form-input" value={f.plataforma} onChange={e => s('plataforma', e.target.value)}>
            <option value="ambos">Google + Meta</option>
            <option value="google">Google Ads</option>
            <option value="meta">Meta Ads</option>
          </select>
        </div>
        <div>
          <label className="form-label">Tipo</label>
          <select className="form-input" value={f.tipo} onChange={e => s('tipo', e.target.value)}>
            <option value="permanente">Permanente</option>
            <option value="sazonal">Sazonal</option>
          </select>
        </div>
        <div>
          <label className="form-label">Objetivo</label>
          <select className="form-input" value={f.objetivo} onChange={e => s('objetivo', e.target.value)}>
            <option value="">Selecione...</option>
            <option value="leads">Geração de Leads</option>
            <option value="vendas">Vendas</option>
            <option value="alcance">Alcance / Branding</option>
            <option value="trafego">Tráfego para site</option>
            <option value="engajamento">Engajamento</option>
          </select>
        </div>
        <div>
          <label className="form-label">Verba total (R$)</label>
          <input className="form-input" type="number" value={f.verba_total} onChange={e => s('verba_total', e.target.value)} placeholder="0,00" />
        </div>
        <div>
          <label className="form-label">Data início</label>
          <input className="form-input" type="date" value={f.data_inicio || ''} onChange={e => s('data_inicio', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Data fim</label>
          <input className="form-input" type="date" value={f.data_fim || ''} onChange={e => s('data_fim', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Status</label>
          <select className="form-input" value={f.status} onChange={e => s('status', e.target.value)}>
            <option value="planejamento">Planejamento</option>
            <option value="ativa">Ativa</option>
            <option value="pausada">Pausada</option>
            <option value="encerrada">Encerrada</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Observações</label>
          <textarea className="form-input" rows={2} value={f.descricao || ''} onChange={e => s('descricao', e.target.value)} placeholder="Segmentação, público-alvo, observações..." />
        </div>
      </div>
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" className="btn-primary flex-1">{inicial ? 'Salvar' : 'Criar Campanha'}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

function FormVerba({ inicial, clientes, onSalvar, onFechar }) {
  const mesAtual = new Date().toISOString().slice(0, 7);
  const [f, setF] = useState(inicial || { cliente_id: '', mes_referencia: mesAtual, verba_google: '', verba_meta: '', gasto_google: 0, gasto_meta: 0 });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(f); }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="form-label">Cliente *</label>
          <select className="form-input" value={f.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
            <option value="">Selecione...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Mês de referência</label>
          <input className="form-input" type="month" value={f.mes_referencia} onChange={e => s('mes_referencia', e.target.value)} />
        </div>
        <div><label className="form-label">Verba Google Ads (R$)</label><input className="form-input" type="number" value={f.verba_google} onChange={e => s('verba_google', e.target.value)} placeholder="0,00" /></div>
        <div><label className="form-label">Gasto atual Google (R$)</label><input className="form-input" type="number" value={f.gasto_google} onChange={e => s('gasto_google', e.target.value)} placeholder="0,00" /></div>
        <div><label className="form-label">Verba Meta Ads (R$)</label><input className="form-input" type="number" value={f.verba_meta} onChange={e => s('verba_meta', e.target.value)} placeholder="0,00" /></div>
        <div><label className="form-label">Gasto atual Meta (R$)</label><input className="form-input" type="number" value={f.gasto_meta} onChange={e => s('gasto_meta', e.target.value)} placeholder="0,00" /></div>
      </div>
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" className="btn-primary flex-1">{inicial ? 'Salvar' : 'Adicionar Verba'}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

function FormDemanda({ inicial, clientes, usuarios, onSalvar, onFechar }) {
  const [f, setF] = useState(inicial || { cliente_id: '', tipo: 'criativo', titulo: '', descricao: '', prazo: '', responsavel_id: '', plataforma: 'ambos', formato: '' });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(f); }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="form-label">Cliente *</label>
          <select className="form-input" value={f.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
            <option value="">Selecione...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Tipo de demanda *</label>
          <select className="form-input" value={f.tipo} onChange={e => s('tipo', e.target.value)}>
            <option value="criativo">Criativo (Design)</option>
            <option value="copy">Copy</option>
            <option value="roteiro">Roteiro</option>
            <option value="captacao">Captação (Filmmaker)</option>
            <option value="edicao">Edição de Vídeo</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Título *</label>
          <input className="form-input" value={f.titulo} onChange={e => s('titulo', e.target.value)} required placeholder="Ex: Stories promoção Dia dos Pais - 3 variações" />
        </div>
        <div>
          <label className="form-label">Plataforma destino</label>
          <select className="form-input" value={f.plataforma} onChange={e => s('plataforma', e.target.value)}>
            <option value="ambos">Google + Meta</option>
            <option value="google">Google Ads</option>
            <option value="meta">Meta Ads</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
        </div>
        <div>
          <label className="form-label">Formato</label>
          <input className="form-input" value={f.formato} onChange={e => s('formato', e.target.value)} placeholder="Ex: 1080x1080, 9:16" />
        </div>
        <div>
          <label className="form-label">Responsável</label>
          <select className="form-input" value={f.responsavel_id} onChange={e => s('responsavel_id', e.target.value)}>
            <option value="">Sem responsável</option>
            {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Prazo *</label>
          <input className="form-input" type="date" value={f.prazo || ''} onChange={e => s('prazo', e.target.value)} required />
        </div>
        <div className="col-span-2">
          <label className="form-label">Briefing / Descrição</label>
          <textarea className="form-input" rows={3} value={f.descricao} onChange={e => s('descricao', e.target.value)} placeholder="Detalhes, referências, tom de voz, CTA..." />
        </div>
      </div>
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" className="btn-primary flex-1">{inicial ? 'Salvar' : 'Solicitar Demanda'}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

function FormReuniao({ inicial, clientes, usuarios, onSalvar, onFechar }) {
  const [f, setF] = useState(inicial || { cliente_id: '', tipo: 'alinhamento', titulo: '', data: '', hora: '', local: '', link_reuniao: '', descricao: '', participantes: [], status: 'agendada', anotacoes: '' });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  const togglePart = uid => setF(p => ({ ...p, participantes: p.participantes.includes(uid) ? p.participantes.filter(id => id !== uid) : [...p.participantes, uid] }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(f); }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="form-label">Cliente *</label>
          <select className="form-input" value={f.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
            <option value="">Selecione...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Tipo</label>
          <select className="form-input" value={f.tipo} onChange={e => s('tipo', e.target.value)}>
            <option value="alinhamento">Alinhamento</option>
            <option value="planejamento">Planejamento de Campanha</option>
            <option value="resultados">Apresentação de Resultados</option>
            <option value="outros">Outros</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Título *</label>
          <input className="form-input" value={f.titulo} onChange={e => s('titulo', e.target.value)} required placeholder="Ex: Resultados Q2 + Planejamento Black Friday" />
        </div>
        <div><label className="form-label">Data *</label><input className="form-input" type="date" value={f.data} onChange={e => s('data', e.target.value)} required /></div>
        <div><label className="form-label">Hora</label><input className="form-input" type="time" value={f.hora || ''} onChange={e => s('hora', e.target.value)} /></div>
        <div><label className="form-label">Local</label><input className="form-input" value={f.local || ''} onChange={e => s('local', e.target.value)} placeholder="Escritório, Online..." /></div>
        <div><label className="form-label">Link (Meet/Zoom)</label><input className="form-input" value={f.link_reuniao || ''} onChange={e => s('link_reuniao', e.target.value)} placeholder="https://..." /></div>
        <div>
          <label className="form-label">Status</label>
          <select className="form-input" value={f.status} onChange={e => s('status', e.target.value)}>
            <option value="agendada">Agendada</option>
            <option value="realizada">Realizada</option>
            <option value="cancelada">Cancelada</option>
            <option value="reagendada">Reagendada</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Participantes</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 12px', background: 'rgba(255,255,255,.03)', borderRadius: 8, border: '1px solid #2A3652' }}>
            {usuarios.map(u => (
              <button key={u.id} type="button" onClick={() => togglePart(u.id)}
                style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, border: 'none', cursor: 'pointer',
                  background: (f.participantes || []).includes(u.id) ? '#6C4CF5' : 'rgba(255,255,255,.06)',
                  color: (f.participantes || []).includes(u.id) ? '#fff' : '#94A3B8',
                  fontWeight: (f.participantes || []).includes(u.id) ? 600 : 400 }}>
                {u.nome.split(' ')[0]}
              </button>
            ))}
          </div>
        </div>
        <div className="col-span-2">
          <label className="form-label">Pauta</label>
          <textarea className="form-input" rows={3} value={f.descricao || ''} onChange={e => s('descricao', e.target.value)} placeholder="Tópicos a serem discutidos..." />
        </div>
        {f.status === 'realizada' && (
          <div className="col-span-2">
            <label className="form-label">Anotações / Ata</label>
            <textarea className="form-input" rows={3} value={f.anotacoes || ''} onChange={e => s('anotacoes', e.target.value)} placeholder="O que foi decidido, próximos passos..." />
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" className="btn-primary flex-1">{inicial ? 'Salvar' : 'Agendar Reunião'}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

// ── ABAS ──────────────────────────────────────────────────────────────────────

function StatCard({ label, value, accent, icon, onClick }) {
  return (
    <button onClick={onClick} style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: '16px 18px', textAlign: 'left', width: '100%', cursor: 'pointer', transition: 'all .2s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#1F2B45'}
      onMouseLeave={e => e.currentTarget.style.background = '#19243A'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</p>
        <span style={{ color: accent }}>{icon}</span>
      </div>
      <p style={{ fontSize: 28, fontWeight: 700, color: '#E2E8F4', lineHeight: 1 }}>{value}</p>
    </button>
  );
}

function AbaVisaoGeral() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState({});

  const fmt = (n) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(n || 0));
  const fmtN = (n) => new Intl.NumberFormat('pt-BR').format(parseInt(n || 0));
  const toggleExp = (clienteId, metricKey) => {
    setExpandido(p => {
      const currentKey = `${clienteId}_${metricKey}`;
      if (p[currentKey]) return { ...p, [currentKey]: false };
      const newState = { ...p };
      ['ativas', 'verba', 'gasto', 'impressoes', 'cliques'].forEach(k => {
        newState[`${clienteId}_${k}`] = false;
      });
      newState[currentKey] = true;
      return newState;
    });
  };
  const isAtiva = (c) => c.status === 'active' || c.status === 'enabled' || c.status === 'ENABLED' || c.status === 'ACTIVE';

  useEffect(() => {
    api.get('/integracoes/resumo')
      .then(r => setClientes(r.data || []))
      .catch(() => toast.error('Erro ao carregar dados.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {loading && <p style={{ color: '#94A3B8', padding: 32, textAlign: 'center' }}>Carregando...</p>}
      {!loading && clientes.length === 0 && (
        <p style={{ color: '#94A3B8', padding: 32, textAlign: 'center' }}>Nenhuma conta integrada. Vá em Integrações para conectar.</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {clientes.map(c => {
          const ativas = c.campanhas.filter(isAtiva);
          const totalGasto = c.campanhas.reduce((a, x) => a + parseFloat(x.valor_gasto || 0), 0);
          const totalImpressoes = c.campanhas.reduce((a, x) => a + parseInt(x.impressoes || 0), 0);
          const totalCliques = c.campanhas.reduce((a, x) => a + parseInt(x.cliques || 0), 0);
          const saldo = parseFloat(c.saldo_conta || 0);

          const metricas = [
            {
              key: 'ativas',
              label: 'Campanhas ativas',
              value: ativas.length,
              color: '#22C55E',
              detalhe: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 0 4px' }}>
                  {ativas.length === 0 && <p style={{ color: '#94A3B8', fontSize: 13 }}>Nenhuma campanha ativa.</p>}
                  {ativas.map(camp => {
                    const orc = parseFloat(camp.orcamento_total || 0) || parseFloat(camp.orcamento_diario || 0) * 30;
                    const orcDiario = parseFloat(camp.orcamento_diario || 0);
                    const gasto = parseFloat(camp.valor_gasto || 0);
                    const pctGasto = orc > 0 ? Math.min((gasto / orc) * 100, 100) : 0;
                    const barColor = pctGasto >= 80 ? '#EF4444' : pctGasto >= 50 ? '#FBBF24' : '#22C55E';
                    const criativos = Array.isArray(camp.criativos) ? camp.criativos : (typeof camp.criativos === 'string' ? JSON.parse(camp.criativos || '[]') : []);
                    const cpc = parseFloat(camp.cpc || 0);
                    return (
                      <div key={camp.id} style={{ background: '#131D31', borderRadius: 12, padding: '16px 18px', border: '1px solid #2A3652' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                          <div>
                            <p style={{ fontSize: 14, fontWeight: 700, color: '#E2E8F0', marginBottom: 4 }}>{camp.nome}</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {camp.objetivo && <span style={{ fontSize: 10, color: '#94A3B8', background: '#1E2D47', padding: '2px 8px', borderRadius: 6 }}>🎯 {camp.objetivo}</span>}
                              {camp.data_inicio && <span style={{ fontSize: 10, color: '#94A3B8', background: '#1E2D47', padding: '2px 8px', borderRadius: 6 }}>📅 Início: {camp.data_inicio}</span>}
                              {camp.data_fim && <span style={{ fontSize: 10, color: '#94A3B8', background: '#1E2D47', padding: '2px 8px', borderRadius: 6 }}>🏁 Fim: {camp.data_fim}</span>}
                            </div>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#22C55E', background: 'rgba(34,197,94,.12)', padding: '4px 12px', borderRadius: 999, flexShrink: 0 }}>● ATIVA</span>
                        </div>

                        {/* Métricas */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                          {[
                            { label: 'Verba total', val: orc > 0 ? fmt(orc) : '—', color: '#E2E8F0' },
                            { label: 'Orçamento diário', val: orcDiario > 0 ? fmt(orcDiario) : '—', color: '#E2E8F0' },
                            { label: 'Gasto no mês', val: fmt(gasto), color: '#6C4CF5' },
                            { label: 'Impressões', val: fmtN(camp.impressoes), color: '#38BDF8' },
                            { label: 'Cliques', val: fmtN(camp.cliques), color: '#FBBF24' },
                            { label: 'CTR', val: `${parseFloat(camp.ctr || 0).toFixed(2)}%`, color: '#FBBF24' },
                            { label: 'CPC médio', val: cpc > 0 ? fmt(cpc) : '—', color: '#A78BFA' },
                            { label: 'Criativos', val: criativos.length, color: '#94A3B8' },
                          ].map(m => (
                            <div key={m.label} style={{ background: '#0F1825', borderRadius: 8, padding: '10px 12px' }}>
                              <p style={{ fontSize: 9, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>{m.label}</p>
                              <p style={{ fontSize: 14, fontWeight: 700, color: m.color }}>{m.val}</p>
                            </div>
                          ))}
                        </div>

                        {/* Barra de consumo */}
                        {orc > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#64748B', marginBottom: 5 }}>
                              <span>Consumo da verba</span>
                              <span style={{ color: barColor, fontWeight: 700 }}>{pctGasto.toFixed(1)}% consumido</span>
                            </div>
                            <div style={{ height: 6, background: '#2A3652', borderRadius: 999 }}>
                              <div style={{ height: '100%', width: `${pctGasto}%`, background: barColor, borderRadius: 999, transition: 'width .3s' }} />
                            </div>
                          </div>
                        )}

                        {/* Criativos */}
                        {criativos.length > 0 && (
                          <div>
                            <p style={{ fontSize: 10, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Criativos ({criativos.length})</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {criativos.map((cr, ci) => (
                                <div key={ci} style={{ background: '#0F1825', borderRadius: 10, padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                  {cr.imagem_url && (
                                    <img
                                      src={cr.imagem_url}
                                      alt={cr.nome}
                                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0, border: '1px solid #2A3652' }}
                                      onError={e => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                      <p style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cr.nome || `Criativo ${ci + 1}`}</p>
                                      <span style={{ fontSize: 10, fontWeight: 600, color: cr.status === 'ACTIVE' || cr.status === 'active' ? '#22C55E' : '#94A3B8', background: cr.status === 'ACTIVE' || cr.status === 'active' ? 'rgba(34,197,94,.1)' : 'rgba(148,163,184,.1)', padding: '2px 8px', borderRadius: 999, flexShrink: 0, marginLeft: 8 }}>{cr.status || '—'}</span>
                                    </div>
                                    {cr.titulo && <p style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 2 }}>{cr.titulo}</p>}
                                    {cr.texto && <p style={{ fontSize: 11, color: '#64748B', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{cr.texto}</p>}
                                    {!cr.titulo && !cr.texto && <p style={{ fontSize: 11, color: '#4A5A78' }}>Sem texto disponível</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {criativos.length === 0 && (
                          <p style={{ fontSize: 11, color: '#4A5A78', fontStyle: 'italic' }}>Sem criativos sincronizados. Sincronize a conta para carregar.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ),
            },
            {
              key: 'verba',
              label: 'Verba na conta',
              value: fmt(saldo),
              color: saldo < 500 ? '#EF4444' : saldo < 1000 ? '#FBBF24' : '#38BDF8',
              detalhe: (() => {
                const transacoes = c.transacoes || [];
                // Agrupa aportes (amount > 0) por mês
                const porMes = {};
                let ultimoAporte = null;
                transacoes.forEach(t => {
                  const val = parseFloat(t.amount || 0) / 100;
                  if (val <= 0) return; // ignora débitos
                  const dt = t.time ? new Date(t.time * 1000) : null;
                  if (!dt) return;
                  if (!ultimoAporte || dt > ultimoAporte.dt) ultimoAporte = { dt, val };
                  const chave = dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                  porMes[chave] = (porMes[chave] || 0) + val;
                });
                const meses = Object.entries(porMes).sort((a, b) => {
                  // ordena do mais recente para o mais antigo
                  const mesesOrdem = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
                  const [ma, ya] = a[0].split(' de ');
                  const [mb, yb] = b[0].split(' de ');
                  return (parseInt(yb) - parseInt(ya)) || (mesesOrdem.indexOf(mb) - mesesOrdem.indexOf(ma));
                });
                return (
                  <div style={{ padding: '14px 0 4px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                      <div style={{ background: '#131D31', borderRadius: 10, padding: '14px 16px' }}>
                        <p style={{ fontSize: 10, color: '#64748B', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Saldo disponível agora</p>
                        <p style={{ fontSize: 22, fontWeight: 700, color: '#38BDF8' }}>{fmt(saldo)}</p>
                      </div>
                      <div style={{ background: '#131D31', borderRadius: 10, padding: '14px 16px' }}>
                        <p style={{ fontSize: 10, color: '#64748B', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Último aporte</p>
                        {ultimoAporte ? (
                          <>
                            <p style={{ fontSize: 18, fontWeight: 700, color: '#22C55E' }}>+{fmt(ultimoAporte.val)}</p>
                            <p style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{ultimoAporte.dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                          </>
                        ) : <p style={{ fontSize: 13, color: '#64748B' }}>Sem registros</p>}
                      </div>
                    </div>
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Aportes por mês</p>
                    {meses.length === 0 && <p style={{ fontSize: 12, color: '#64748B' }}>Extrato não disponível. Sincronize a conta para atualizar.</p>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {meses.map(([mes, total]) => (
                        <div key={mes} style={{ background: '#131D31', borderRadius: 8, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, color: '#CBD5E1', textTransform: 'capitalize' }}>{mes}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#22C55E' }}>+{fmt(total)}</span>
                        </div>
                      ))}
                    </div>
                    {transacoes.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <p style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Todas as movimentações</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {transacoes.map((t, i) => {
                            const val = parseFloat(t.amount || 0) / 100;
                            const dt = t.time ? new Date(t.time * 1000) : null;
                            return (
                              <div key={i} style={{ background: '#131D31', borderRadius: 8, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                <span style={{ color: '#94A3B8' }}>
                                  {dt ? dt.toLocaleDateString('pt-BR') : '—'} · {t.type || 'transação'}
                                </span>
                                <span style={{ color: val > 0 ? '#22C55E' : '#EF4444', fontWeight: 600 }}>
                                  {val > 0 ? '+' : ''}{fmt(val)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })(),
            },
            {
              key: 'gasto',
              label: 'Total gasto no mês',
              value: fmt(totalGasto),
              color: '#6C4CF5',
              detalhe: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 0 4px' }}>
                  {c.campanhas.filter(x => parseFloat(x.valor_gasto) > 0).sort((a, b) => parseFloat(b.valor_gasto) - parseFloat(a.valor_gasto)).map(camp => {
                    const pctVal = totalGasto > 0 ? (parseFloat(camp.valor_gasto) / totalGasto) * 100 : 0;
                    return (
                      <div key={camp.id} style={{ background: '#131D31', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{camp.nome}</p>
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#6C4CF5' }}>{fmt(camp.valor_gasto)}</p>
                        </div>
                        <div style={{ height: 4, background: '#2A3652', borderRadius: 999 }}>
                          <div style={{ height: '100%', width: `${pctVal}%`, background: '#6C4CF5', borderRadius: 999 }} />
                        </div>
                        <p style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>{pctVal.toFixed(1)}% do total gasto</p>
                      </div>
                    );
                  })}
                </div>
              ),
            },
            {
              key: 'impressoes',
              label: 'Impressões',
              value: fmtN(totalImpressoes),
              color: '#38BDF8',
              detalhe: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 0 4px' }}>
                  {c.campanhas.filter(x => parseInt(x.impressoes) > 0).sort((a, b) => parseInt(b.impressoes) - parseInt(a.impressoes)).map(camp => {
                    const pctVal = totalImpressoes > 0 ? (parseInt(camp.impressoes) / totalImpressoes) * 100 : 0;
                    return (
                      <div key={camp.id} style={{ background: '#131D31', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{camp.nome}</p>
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#38BDF8' }}>{fmtN(camp.impressoes)}</p>
                        </div>
                        <div style={{ height: 4, background: '#2A3652', borderRadius: 999 }}>
                          <div style={{ height: '100%', width: `${pctVal}%`, background: '#38BDF8', borderRadius: 999 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            },
            {
              key: 'cliques',
              label: 'Cliques',
              value: fmtN(totalCliques),
              color: '#FBBF24',
              detalhe: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 0 4px' }}>
                  {c.campanhas.filter(x => parseInt(x.cliques) > 0).sort((a, b) => parseInt(b.cliques) - parseInt(a.cliques)).map(camp => {
                    const pctVal = totalCliques > 0 ? (parseInt(camp.cliques) / totalCliques) * 100 : 0;
                    return (
                      <div key={camp.id} style={{ background: '#131D31', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{camp.nome}</p>
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#FBBF24' }}>{fmtN(camp.cliques)} cliques · CTR {parseFloat(camp.ctr || 0).toFixed(2)}%</p>
                        </div>
                        <div style={{ height: 4, background: '#2A3652', borderRadius: 999 }}>
                          <div style={{ height: '100%', width: `${pctVal}%`, background: '#FBBF24', borderRadius: 999 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            },
          ];

          const detalheAtivo = metricas.find(m => expandido[`${c.cliente_id}_${m.key}`]);

          return (
            <div key={c.cliente_id} style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 16, overflow: 'hidden' }}>
              {/* Header do cliente */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #2A3652', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#6C4CF5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: 16, flexShrink: 0 }}>
                  {c.cliente_nome?.charAt(0)}
                </div>
                <div>
                  <p style={{ fontSize: 15, fontWeight: 700, color: '#E2E8F0' }}>{c.cliente_nome}</p>
                  <p style={{ fontSize: 11, color: '#64748B' }}>{(c.plataformas || []).map(p => p === 'meta' ? '📘 Meta Ads' : '🎯 Google Ads').join(' · ')}</p>
                </div>
                {saldo < 500 && saldo > 0 && (
                  <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'rgba(239,68,68,.12)', padding: '4px 10px', borderRadius: 999 }}>⚠️ Verba baixa</span>
                )}
              </div>
              {/* Métricas clicáveis */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', borderTop: '1px solid #2A3652' }}>
                {metricas.map((m, idx) => (
                  <button
                    key={m.key}
                    onClick={() => toggleExp(c.cliente_id, m.key)}
                    style={{
                      width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                      borderRight: idx < 4 ? '1px solid #2A3652' : 'none',
                      padding: '16px 12px', textAlign: 'center',
                      borderBottom: expandido[`${c.cliente_id}_${m.key}`] ? `2px solid ${m.color}` : '2px solid transparent',
                      transition: 'all .15s',
                    }}
                  >
                    <p style={{ fontSize: 10, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>{m.label}</p>
                    <p style={{ fontSize: 20, fontWeight: 700, color: m.color }}>{m.value}</p>
                    <p style={{ fontSize: 10, color: expandido[`${c.cliente_id}_${m.key}`] ? m.color : '#4A5A78', marginTop: 4 }}>
                      {expandido[`${c.cliente_id}_${m.key}`] ? '▲ fechar' : '▼ ver detalhes'}
                    </p>
                  </button>
                ))}
              </div>
              {/* Painel expandido */}
              {detalheAtivo && (
                <div style={{ padding: '16px 20px', borderTop: `1px solid #2A3652` }}>
                  {detalheAtivo.detalhe}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AbaCampanhas({ clientes, onRefresh }) {
  const [campanhas, setCampanhas] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroPlat, setFiltroPlat] = useState('');

  const carregar = useCallback(async () => {
    try { const { data } = await api.get('/trafego/campanhas'); setCampanhas(data || []); }
    catch { toast.error('Erro ao carregar campanhas.'); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtradas = useMemo(() => campanhas.filter(c =>
    (!filtroStatus || c.status === filtroStatus) &&
    (!filtroCliente || c.cliente_id === filtroCliente) &&
    (!filtroPlat || c.plataforma === filtroPlat)
  ), [campanhas, filtroStatus, filtroCliente, filtroPlat]);

  const salvar = async dados => {
    try {
      if (editando) { await api.patch(`/trafego/campanhas/${editando.id}`, dados); toast.success('Campanha atualizada!'); setEditando(null); }
      else { await api.post('/trafego/campanhas', dados); toast.success('Campanha criada!'); setModal(false); }
      carregar(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const toggleStatus = async (c) => {
    const novoStatus = c.status === 'ativa' ? 'pausada' : 'ativa';
    try { await api.patch(`/trafego/campanhas/${c.id}`, { status: novoStatus }); toast.success(`Campanha ${novoStatus}!`); carregar(); onRefresh(); }
    catch { toast.error('Erro.'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir campanha?')) return;
    try { await api.delete(`/trafego/campanhas/${id}`); toast.success('Excluída!'); carregar(); onRefresh(); }
    catch { toast.error('Erro.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 'auto' }} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Todos clientes</option>
          {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos status</option>
          <option value="planejamento">Planejamento</option>
          <option value="ativa">Ativa</option>
          <option value="pausada">Pausada</option>
          <option value="encerrada">Encerrada</option>
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filtroPlat} onChange={e => setFiltroPlat(e.target.value)}>
          <option value="">Todas plataformas</option>
          <option value="google">Google Ads</option>
          <option value="meta">Meta Ads</option>
          <option value="ambos">Google + Meta</option>
        </select>
        <button onClick={() => setModal(true)} className="btn-primary" style={{ marginLeft: 'auto' }}>+ Nova Campanha</button>
      </div>
      {filtradas.length === 0
        ? <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748B' }}><p style={{ fontSize: 14 }}>Nenhuma campanha encontrada.</p></div>
        : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtradas.map(c => {
              const p = pct(c.gasto_realizado, c.verba_total);
              return (
                <div key={c.id} style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#E2E8F4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</p>
                      <p style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{c.cliente_fantasia || c.cliente_nome || '—'}</p>
                    </div>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, background: STATUS_BG[c.status], color: STATUS_COR[c.status], fontWeight: 700, marginLeft: 8, flexShrink: 0 }}>{c.status}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 6, background: PLAT_BG[c.plataforma], color: PLAT_COR[c.plataforma], fontWeight: 700 }}>{PLAT_LABEL[c.plataforma]}</span>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 6, background: 'rgba(255,255,255,.05)', color: '#94A3B8' }}>{c.tipo}</span>
                    {c.objetivo && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 6, background: 'rgba(255,255,255,.05)', color: '#94A3B8' }}>{c.objetivo}</span>}
                  </div>
                  {(c.data_inicio || c.data_fim) && <p style={{ fontSize: 11, color: '#64748B', marginBottom: 8 }}>{c.data_inicio || '?'}{c.data_fim ? ` → ${c.data_fim}` : ' (sem fim)'}</p>}
                  {c.verba_total > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748B', marginBottom: 4 }}>
                        <span>Verba utilizada</span>
                        <span style={{ color: p >= 90 ? '#F87171' : p >= 75 ? '#FBBF24' : '#94A3B8' }}>{moeda(c.gasto_realizado)} / {moeda(c.verba_total)} ({p}%)</span>
                      </div>
                      <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,.06)' }}>
                        <div style={{ height: '100%', borderRadius: 999, background: p >= 90 ? '#F87171' : p >= 75 ? '#FBBF24' : '#6C4CF5', width: `${p}%` }} />
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,.05)' }}>
                    <button onClick={() => setEditando(c)} style={{ fontSize: 12, color: '#6C4CF5', background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button>
                    <button onClick={() => toggleStatus(c)} style={{ fontSize: 12, color: '#94A3B8', background: 'none', border: 'none', cursor: 'pointer' }}>{c.status === 'ativa' ? 'Pausar' : 'Ativar'}</button>
                    <button onClick={() => excluir(c.id)} style={{ fontSize: 12, color: '#F87171', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}>Excluir</button>
                  </div>
                </div>
              );
            })}
          </div>
      }
      <Modal open={modal || !!editando} onClose={() => { setModal(false); setEditando(null); }} title={editando ? 'Editar Campanha' : 'Nova Campanha'} size="lg">
        <FormCampanha inicial={editando} clientes={clientes} onSalvar={salvar} onFechar={() => { setModal(false); setEditando(null); }} />
      </Modal>
    </div>
  );
}

function AbaVerbas({ clientes, onRefresh }) {
  const mesAtual = new Date().toISOString().slice(0, 7);
  const [verbas, setVerbas] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroMes, setFiltroMes] = useState(mesAtual);

  const carregar = useCallback(async () => {
    try { const { data } = await api.get('/trafego/verbas', { params: { mes: filtroMes } }); setVerbas(data || []); }
    catch { toast.error('Erro ao carregar verbas.'); }
  }, [filtroMes]);

  useEffect(() => { carregar(); }, [carregar]);

  const filtradas = useMemo(() =>
    [...verbas].sort((a, b) => Math.max(pct(b.gasto_google, b.verba_google), pct(b.gasto_meta, b.verba_meta)) - Math.max(pct(a.gasto_google, a.verba_google), pct(a.gasto_meta, a.verba_meta))),
    [verbas]
  );

  const salvar = async dados => {
    try {
      if (editando) { await api.patch(`/trafego/verbas/${editando.id}`, dados); toast.success('Verba atualizada!'); setEditando(null); }
      else { await api.post('/trafego/verbas', dados); toast.success('Verba cadastrada!'); setModal(false); }
      carregar(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const excluir = async id => {
    if (!window.confirm('Excluir verba?')) return;
    try { await api.delete(`/trafego/verbas/${id}`); toast.success('Excluída!'); carregar(); onRefresh(); }
    catch { toast.error('Erro.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, alignItems: 'center' }}>
        <input type="month" className="form-input" style={{ width: 'auto' }} value={filtroMes} onChange={e => setFiltroMes(e.target.value)} />
        <button onClick={() => setModal(true)} className="btn-primary" style={{ marginLeft: 'auto' }}>+ Adicionar Verba</button>
      </div>
      {filtradas.length === 0
        ? <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748B' }}><p style={{ fontSize: 14 }}>Nenhuma verba cadastrada para este mês.</p></div>
        : <div style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, overflow: 'hidden' }}>
            <div className="resp-table-wrap" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 700 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #2A3652', background: 'rgba(255,255,255,.02)' }}>
                    {['Cliente','Google Ads','','Meta Ads','','Ações'].map((h, i) => (
                      <th key={i} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map(v => {
                    const pG = pct(v.gasto_google, v.verba_google);
                    const pM = pct(v.gasto_meta, v.verba_meta);
                    const corG = pG >= 90 ? '#F87171' : pG >= 75 ? '#FBBF24' : '#4ADE80';
                    const corM = pM >= 90 ? '#F87171' : pM >= 75 ? '#FBBF24' : '#4ADE80';
                    return (
                      <tr key={v.id} style={{ borderBottom: '1px solid rgba(42,54,82,.5)' }}>
                        <td style={{ padding: '14px 16px', color: '#E2E8F4', fontWeight: 500 }}>{v.cliente_fantasia || v.cliente_nome || '—'}</td>
                        <td style={{ padding: '14px 16px', minWidth: 160 }}>
                          {v.verba_google > 0 ? (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94A3B8', marginBottom: 4 }}><span>{moeda(v.gasto_google)}</span><span>{moeda(v.verba_google)}</span></div>
                              <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,.06)' }}><div style={{ height: '100%', borderRadius: 999, background: corG, width: `${pG}%` }} /></div>
                            </div>
                          ) : <span style={{ color: '#3d4f72', fontSize: 12 }}>Não configurado</span>}
                        </td>
                        <td style={{ padding: '14px 8px', color: corG, fontWeight: 700, fontSize: 12 }}>{v.verba_google > 0 ? `${pG}%` : '—'}</td>
                        <td style={{ padding: '14px 16px', minWidth: 160 }}>
                          {v.verba_meta > 0 ? (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94A3B8', marginBottom: 4 }}><span>{moeda(v.gasto_meta)}</span><span>{moeda(v.verba_meta)}</span></div>
                              <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,.06)' }}><div style={{ height: '100%', borderRadius: 999, background: corM, width: `${pM}%` }} /></div>
                            </div>
                          ) : <span style={{ color: '#3d4f72', fontSize: 12 }}>Não configurado</span>}
                        </td>
                        <td style={{ padding: '14px 8px', color: corM, fontWeight: 700, fontSize: 12 }}>{v.verba_meta > 0 ? `${pM}%` : '—'}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setEditando(v)} style={{ fontSize: 12, color: '#6C4CF5', background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button>
                            <button onClick={() => excluir(v.id)} style={{ fontSize: 12, color: '#F87171', background: 'none', border: 'none', cursor: 'pointer' }}>Excluir</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
      }
      <Modal open={modal || !!editando} onClose={() => { setModal(false); setEditando(null); }} title={editando ? 'Editar Verba' : 'Adicionar Verba'} size="md">
        <FormVerba inicial={editando} clientes={clientes} onSalvar={salvar} onFechar={() => { setModal(false); setEditando(null); }} />
      </Modal>
    </div>
  );
}

function AbaDemandas({ clientes, usuarios, onRefresh }) {
  const [demandas, setDemandas] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  const carregar = useCallback(async () => {
    try { const { data } = await api.get('/trafego/demandas'); setDemandas(data || []); }
    catch { toast.error('Erro ao carregar demandas.'); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtradas = useMemo(() =>
    demandas.filter(d => (!filtroStatus || d.status === filtroStatus) && (!filtroTipo || d.tipo === filtroTipo)),
    [demandas, filtroStatus, filtroTipo]
  );

  const salvar = async dados => {
    try {
      if (editando) { await api.patch(`/trafego/demandas/${editando.id}`, dados); toast.success('Demanda atualizada!'); setEditando(null); }
      else { await api.post('/trafego/demandas', dados); toast.success('Demanda criada!'); setModal(false); }
      carregar(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const atualizarStatus = async (id, status) => {
    try { await api.patch(`/trafego/demandas/${id}`, { status }); toast.success('Status atualizado!'); carregar(); onRefresh(); }
    catch { toast.error('Erro.'); }
  };

  const excluir = async id => {
    if (!window.confirm('Excluir demanda?')) return;
    try { await api.delete(`/trafego/demandas/${id}`); toast.success('Excluída!'); carregar(); onRefresh(); }
    catch { toast.error('Erro.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 'auto' }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {Object.entries(TIPO_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos status</option>
          <option value="pendente">Pendente</option>
          <option value="em_andamento">Em andamento</option>
          <option value="entregue">Entregue</option>
          <option value="aprovado">Aprovado</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <button onClick={() => setModal(true)} className="btn-primary" style={{ marginLeft: 'auto' }}>+ Solicitar Demanda</button>
      </div>
      {filtradas.length === 0
        ? <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748B' }}><p style={{ fontSize: 14 }}>Nenhuma demanda criativa cadastrada.</p></div>
        : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtradas.map(d => {
              const dias = d.prazo ? Math.ceil((new Date(d.prazo) - new Date()) / 86400000) : null;
              const corPrazo = dias === null ? '#64748B' : dias < 0 ? '#F87171' : dias <= 1 ? '#FBBF24' : '#64748B';
              return (
                <div key={d.id} style={{ background: '#19243A', border: `1px solid ${dias !== null && dias < 0 ? 'rgba(248,113,113,.3)' : '#2A3652'}`, borderRadius: 14, padding: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: `${TIPO_COR[d.tipo]}18`, color: TIPO_COR[d.tipo], fontWeight: 700 }}>{TIPO_LABEL[d.tipo] || d.tipo}</span>
                    <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: `${DEM_ST_COR[d.status]}18`, color: DEM_ST_COR[d.status], fontWeight: 700 }}>{d.status.replace('_', ' ')}</span>
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#E2E8F4', marginBottom: 4 }}>{d.titulo}</p>
                  <p style={{ fontSize: 12, color: '#64748B', marginBottom: 8 }}>{d.cliente_fantasia || d.cliente_nome || '—'}</p>
                  {d.descricao && <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.descricao}</p>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {d.plataforma && <span style={{ fontSize: 10, color: '#64748B', background: 'rgba(255,255,255,.05)', padding: '2px 7px', borderRadius: 6 }}>{d.plataforma}</span>}
                    {d.formato && <span style={{ fontSize: 10, color: '#64748B', background: 'rgba(255,255,255,.05)', padding: '2px 7px', borderRadius: 6 }}>{d.formato}</span>}
                    {d.responsavel_nome && <span style={{ fontSize: 10, color: '#94A3B8', background: 'rgba(255,255,255,.05)', padding: '2px 7px', borderRadius: 6 }}>{d.responsavel_nome.split(' ')[0]}</span>}
                  </div>
                  {d.prazo && <p style={{ fontSize: 11, color: corPrazo, marginBottom: 10, fontWeight: dias !== null && dias <= 1 ? 700 : 400 }}>
                    {dias < 0 ? `${Math.abs(dias)}d em atraso` : dias === 0 ? 'Prazo hoje!' : `Prazo: ${d.prazo}`}
                  </p>}
                  <div style={{ display: 'flex', gap: 6, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,.05)', flexWrap: 'wrap' }}>
                    {['em_andamento','entregue','aprovado'].filter(s => d.status !== s).map(s => (
                      <button key={s} onClick={() => atualizarStatus(d.id, s)}
                        style={{ fontSize: 11, color: DEM_ST_COR[s], background: `${DEM_ST_COR[s]}15`, border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
                        {s === 'em_andamento' ? 'Em andamento' : s === 'entregue' ? 'Entregue' : 'Aprovar'}
                      </button>
                    ))}
                    <button onClick={() => setEditando(d)} style={{ fontSize: 11, color: '#94A3B8', background: 'rgba(255,255,255,.05)', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginLeft: 'auto' }}>Editar</button>
                    <button onClick={() => excluir(d.id)} style={{ fontSize: 11, color: '#F87171', background: 'rgba(248,113,113,.1)', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>Excluir</button>
                  </div>
                </div>
              );
            })}
          </div>
      }
      <Modal open={modal || !!editando} onClose={() => { setModal(false); setEditando(null); }} title={editando ? 'Editar Demanda' : 'Solicitar Demanda Criativa'} size="lg">
        <FormDemanda inicial={editando} clientes={clientes} usuarios={usuarios} onSalvar={salvar} onFechar={() => { setModal(false); setEditando(null); }} />
      </Modal>
    </div>
  );
}

function AbaReunioes({ clientes, usuarios, onRefresh }) {
  const [reunioes, setReunioes] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [verAta, setVerAta] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const carregar = useCallback(async () => {
    try { const { data } = await api.get('/trafego/reunioes'); setReunioes(data || []); }
    catch { toast.error('Erro ao carregar reuniões.'); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtradas = useMemo(() =>
    reunioes.filter(r => (!filtroTipo || r.tipo === filtroTipo) && (!filtroStatus || r.status === filtroStatus))
      .sort((a, b) => new Date(b.data + 'T' + (b.hora || '00:00')) - new Date(a.data + 'T' + (a.hora || '00:00'))),
    [reunioes, filtroTipo, filtroStatus]
  );

  const proximas = filtradas.filter(r => r.status === 'agendada' && r.data >= hoje());
  const passadas = filtradas.filter(r => r.status !== 'agendada' || r.data < hoje());

  const salvar = async dados => {
    try {
      if (editando) { await api.patch(`/trafego/reunioes/${editando.id}`, dados); toast.success('Reunião atualizada!'); setEditando(null); }
      else { await api.post('/trafego/reunioes', dados); toast.success('Reunião agendada!'); setModal(false); }
      carregar(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const marcarRealizada = async id => {
    try { await api.patch(`/trafego/reunioes/${id}`, { status: 'realizada' }); toast.success('Marcada como realizada!'); carregar(); }
    catch { toast.error('Erro.'); }
  };

  const excluir = async id => {
    if (!window.confirm('Excluir reunião?')) return;
    try { await api.delete(`/trafego/reunioes/${id}`); toast.success('Excluída!'); carregar(); }
    catch { toast.error('Erro.'); }
  };

  const ReuniaoCard = ({ r }) => {
    const diasAte = Math.ceil((new Date(r.data) - new Date()) / 86400000);
    const corDias = diasAte < 0 ? '#64748B' : diasAte === 0 ? '#FBBF24' : diasAte <= 2 ? '#F97316' : '#94A3B8';
    const parts = (r.participantes || []).map(pid => usuarios.find(u => u.id === pid)).filter(Boolean);
    return (
      <div style={{ background: '#19243A', border: `1px solid ${r.status === 'agendada' && diasAte <= 2 && diasAte >= 0 ? 'rgba(249,115,22,.35)' : '#2A3652'}`, borderRadius: 14, padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: `${REUN_TIPO_COR[r.tipo]}18`, color: REUN_TIPO_COR[r.tipo], fontWeight: 700 }}>{REUN_TIPO_LABEL[r.tipo]}</span>
            <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: `${REUN_ST_COR[r.status]}18`, color: REUN_ST_COR[r.status], fontWeight: 700 }}>{r.status}</span>
          </div>
          {r.status === 'agendada' && <span style={{ fontSize: 11, color: corDias, fontWeight: 700 }}>{diasAte < 0 ? 'Atrasada' : diasAte === 0 ? 'Hoje!' : `${diasAte}d`}</span>}
        </div>
        <p style={{ fontSize: 14, fontWeight: 600, color: '#E2E8F4', marginBottom: 4 }}>{r.titulo}</p>
        <p style={{ fontSize: 12, color: '#64748B', marginBottom: 8 }}>{r.cliente_fantasia || r.cliente_nome || '—'}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          {r.data && <span style={{ fontSize: 12, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Ico d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" size={12} />
            {r.data}{r.hora ? ` ${r.hora}` : ''}
          </span>}
          {r.local && <span style={{ fontSize: 12, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {r.local}
          </span>}
        </div>
        {parts.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
            {parts.map(u => <span key={u.id} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: 'rgba(108,76,245,.15)', color: '#A78BFA' }}>{u.nome.split(' ')[0]}</span>)}
          </div>
        )}
        {r.descricao && <p style={{ fontSize: 12, color: '#64748B', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.descricao}</p>}
        <div style={{ display: 'flex', gap: 6, paddingTop: 10, marginTop: 10, borderTop: '1px solid rgba(255,255,255,.05)', flexWrap: 'wrap', alignItems: 'center' }}>
          {r.link_reuniao && <a href={r.link_reuniao} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#38BDF8', background: 'rgba(56,189,248,.1)', borderRadius: 6, padding: '3px 8px', textDecoration: 'none' }}>Abrir Link</a>}
          {r.status === 'agendada' && <button onClick={() => marcarRealizada(r.id)} style={{ fontSize: 11, color: '#4ADE80', background: 'rgba(74,222,128,.1)', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>Marcar Realizada</button>}
          {r.anotacoes && <button onClick={() => setVerAta(r)} style={{ fontSize: 11, color: '#A78BFA', background: 'rgba(167,139,250,.1)', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>Ver Ata</button>}
          <button onClick={() => setEditando(r)} style={{ fontSize: 11, color: '#94A3B8', background: 'rgba(255,255,255,.05)', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginLeft: 'auto' }}>Editar</button>
          <button onClick={() => excluir(r.id)} style={{ fontSize: 11, color: '#F87171', background: 'rgba(248,113,113,.1)', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>Excluir</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 'auto' }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {Object.entries(REUN_TIPO_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos status</option>
          <option value="agendada">Agendada</option>
          <option value="realizada">Realizada</option>
          <option value="cancelada">Cancelada</option>
          <option value="reagendada">Reagendada</option>
        </select>
        <button onClick={() => setModal(true)} className="btn-primary" style={{ marginLeft: 'auto' }}>+ Agendar Reunião</button>
      </div>

      {proximas.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Próximas Reuniões</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{proximas.map(r => <ReuniaoCard key={r.id} r={r} />)}</div>
        </div>
      )}
      {passadas.length > 0 && (
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Histórico</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{passadas.map(r => <ReuniaoCard key={r.id} r={r} />)}</div>
        </div>
      )}
      {filtradas.length === 0 && <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748B' }}><p style={{ fontSize: 14 }}>Nenhuma reunião cadastrada.</p></div>}

      <Modal open={modal || !!editando} onClose={() => { setModal(false); setEditando(null); }} title={editando ? 'Editar Reunião' : 'Agendar Reunião'} size="lg">
        <FormReuniao inicial={editando} clientes={clientes} usuarios={usuarios} onSalvar={salvar} onFechar={() => { setModal(false); setEditando(null); }} />
      </Modal>
      <Modal open={!!verAta} onClose={() => setVerAta(null)} title="Ata da Reunião" size="md">
        {verAta && (
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#E2E8F4', marginBottom: 4 }}>{verAta.titulo}</p>
            <p style={{ fontSize: 12, color: '#64748B', marginBottom: 16 }}>{verAta.data}{verAta.hora ? ` ${verAta.hora}` : ''}</p>
            <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid #2A3652', borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{verAta.anotacoes}</p>
            </div>
            <button onClick={() => setVerAta(null)} className="btn-secondary" style={{ marginTop: 16, width: '100%' }}>Fechar</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── ABA INTEGRAÇÕES ───────────────────────────────────────────────────────────

function AbaIntegracoes({ onSync, isAdmin }) {
  const [integracoes, setIntegracoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ cliente_id: '', plataforma: 'meta', access_token: '', account_id: '', developer_token: '', manager_customer_id: '' });
  const [syncing, setSyncing] = useState(null);
  const [editando, setEditando] = useState(null);
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const carregar = async () => {
    try {
      const [ints, cls] = await Promise.all([
        api.get('/integracoes'),
        api.get('/clientes/lista'),
      ]);
      setIntegracoes(ints.data || []);
      setClientes(cls.data || []);
    } catch {}
  };

  useEffect(() => { carregar(); }, []);

  const salvar = async (e) => {
    e.preventDefault();
    try {
      if (editando) {
        await api.patch(`/integracoes/${editando.id}`, form);
        toast.success('Integração atualizada!');
        setEditando(null);
      } else {
        await api.post('/integracoes', form);
        toast.success('Integração salva!');
      }
      setModal(false);
      setForm({ cliente_id: '', plataforma: 'meta', access_token: '', account_id: '', developer_token: '', manager_customer_id: '' });
      carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar.'); }
  };

  const sincronizar = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/integracoes/${id}/sync`);
      toast.success(`${data.sincronizadas} campanhas sincronizadas!`);
      carregar();
      if (onSync) onSync();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao sincronizar.'); }
    finally { setSyncing(null); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Remover esta integração?')) return;
    await api.delete(`/integracoes/${id}`);
    carregar();
  };

  const abrirEditar = (integ) => {
    setEditando(integ);
    setForm({ cliente_id: integ.cliente_id, plataforma: integ.plataforma, access_token: integ.access_token || '', account_id: integ.account_id || '', developer_token: integ.developer_token || '', manager_customer_id: integ.manager_customer_id || '' });
    setModal(true);
  };

  const PLAT_COR_INT = { meta: '#1877F2', google: '#EA4335' };
  const PLAT_LABEL_INT = { meta: 'Meta Ads', google: 'Google Ads' };

  return (
    <div>
      {/* Contas conectadas */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#E2E8F0' }}>Contas Conectadas</h3>
        <button className="btn-primary" onClick={() => { setEditando(null); setForm({ cliente_id: '', plataforma: 'meta', access_token: '', account_id: '', developer_token: '', manager_customer_id: '' }); setModal(true); }}>+ Conectar Conta</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
        {integracoes.length === 0 && (
          <p style={{ color: '#94A3B8', fontSize: 13 }}>Nenhuma conta conectada ainda.</p>
        )}
        {integracoes.map(integ => (
          <div key={integ.id} style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 14, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: PLAT_COR_INT[integ.plataforma], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                {integ.plataforma === 'meta' ? '📘' : '🎯'}
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0' }}>{integ.cliente_nome || integ.razao_social}</p>
                <p style={{ fontSize: 11, color: PLAT_COR_INT[integ.plataforma] }}>{PLAT_LABEL_INT[integ.plataforma]}</p>
              </div>
            </div>
            <p style={{ fontSize: 11, color: '#64748B', marginBottom: 2 }}>Conta: {integ.account_id}</p>
            <p style={{ fontSize: 11, color: '#64748B', marginBottom: 12 }}>
              {integ.ultima_sincronizacao ? `Sincronizado: ${new Date(integ.ultima_sincronizacao).toLocaleString('pt-BR')}` : 'Nunca sincronizado'}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => sincronizar(integ.id)} disabled={syncing === integ.id}
                style={{ flex: 1, fontSize: 12, padding: '6px 0', background: '#6C4CF5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                {syncing === integ.id ? '⟳ Sincronizando...' : '⟳ Sincronizar'}
              </button>
              <button onClick={() => abrirEditar(integ)}
                style={{ fontSize: 12, padding: '6px 12px', background: '#1F2B45', color: '#94A3B8', border: '1px solid #2A3652', borderRadius: 8, cursor: 'pointer' }}>
                Editar
              </button>
              <button onClick={() => excluir(integ.id)}
                style={{ fontSize: 12, padding: '6px 12px', background: 'none', color: '#EF4444', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                Remover
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal conectar / editar */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 20, padding: 28, width: '100%', maxWidth: 520 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#E2E8F0', marginBottom: 20 }}>{editando ? 'Editar Integração' : 'Conectar Conta de Anúncios'}</h3>
            <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="form-label">Cliente *</label>
                <select className="form-input" value={form.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
                  <option value="">Selecione o cliente</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Plataforma *</label>
                <select className="form-input" value={form.plataforma} onChange={e => s('plataforma', e.target.value)}>
                  <option value="meta">Meta Ads (Facebook/Instagram)</option>
                  <option value="google">Google Ads</option>
                </select>
              </div>
              <div>
                <label className="form-label">Access Token *</label>
                <input className="form-input" value={form.access_token} onChange={e => s('access_token', e.target.value)} required placeholder={form.plataforma === 'meta' ? 'Token de acesso do Meta Business Suite' : 'OAuth2 Access Token do Google'} />
                {form.plataforma === 'meta' && (
                  <p style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                    Obtenha em: Meta Business Suite → Configurações → Tokens de acesso do sistema
                  </p>
                )}
              </div>
              <div>
                <label className="form-label">{form.plataforma === 'meta' ? 'ID da Conta de Anúncio (act_XXXXXXX) *' : 'Customer ID (XXX-XXX-XXXX) *'}</label>
                <input className="form-input" value={form.account_id} onChange={e => s('account_id', e.target.value)} required placeholder={form.plataforma === 'meta' ? 'act_1234567890' : '123-456-7890'} />
              </div>
              {form.plataforma === 'google' && (
                <>
                  <div>
                    <label className="form-label">Developer Token *</label>
                    <input className="form-input" value={form.developer_token} onChange={e => s('developer_token', e.target.value)} placeholder="Token de desenvolvedor do Google Ads" />
                  </div>
                  <div>
                    <label className="form-label">Manager Customer ID (MCC) — opcional</label>
                    <input className="form-input" value={form.manager_customer_id} onChange={e => s('manager_customer_id', e.target.value)} placeholder="ID da conta gerenciadora (se houver)" />
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>Salvar</button>
                <button type="button" className="btn-secondary" onClick={() => { setModal(false); setEditando(null); }}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────────

export default function Trafego() {
  const usuario = useAppStore(s => s.usuario);
  const isAdmin = parseInt(usuario?.nivel_acesso) <= 1;
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [demandas, setDemandas] = useState([]);
  const [reunioes, setReunioes] = useState([]);
  const [aba, setAba] = useState('overview');

  const carregarTudo = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([
        api.get('/trafego/demandas'),
        api.get('/trafego/reunioes'),
      ]);
      setDemandas(d.data || []);
      setReunioes(r.data || []);
    } catch { /* silencioso — cada aba trata seus próprios erros */ }
  }, []);

  const verificarAlertasVerba = useCallback(async () => {
    try {
      const { data: campanhas } = await api.get('/integracoes/campanhas');
      const destinatario_id = usuario?.id;
      if (!destinatario_id) return;
      for (const c of (campanhas || [])) {
        const orcTotal = parseFloat(c.orcamento_total || 0) || parseFloat(c.orcamento_diario || 0) * 30;
        if (orcTotal > 0 && parseFloat(c.valor_gasto || 0) / orcTotal >= 0.8) {
          const pct = Math.round((parseFloat(c.valor_gasto || 0) / orcTotal) * 100);
          await api.post('/notificacoes', {
            destinatario_id,
            tipo: 'alerta_verba',
            titulo: `Alerta de verba: ${c.nome}`,
            mensagem: `A campanha ${c.nome} do cliente ${c.cliente_nome} está com ${pct}% da verba consumida.`,
          }).catch(() => {});
        }
      }
    } catch { /* silencioso */ }
  }, [usuario]);

  useEffect(() => {
    carregarTudo();
    verificarAlertasVerba();
    api.get('/clientes/lista').then(r => setClientes(r.data || [])).catch(() => {});
    api.get('/usuarios/equipe').then(r => setUsuarios(r.data || [])).catch(() => {});
  }, [carregarTudo, verificarAlertasVerba]);

  const TABS = [
    { k: 'overview',    l: 'Visão Geral',       icon: <Ico d={['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z','M9 22V12h6v10']} size={14} /> },
    { k: 'integracoes', l: '🔗 Integrações', icon: null },
    { k: 'demandas',   l: 'Demandas Criativas', icon: <Ico d={['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z','M14 2v6h6','M16 13H8']} size={14} /> },
    { k: 'reunioes',   l: 'Reuniões',           icon: <Ico d={['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2','M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z','M23 21v-2a4 4 0 0 0-3-3.87','M16 3.13a4 4 0 0 1 0 7.75']} size={14} /> },
  ];

  return (
    <div>
      <PageHeader title="Tráfego Pago" subtitle="Campanhas, integrações, demandas e reuniões" />
      <Tabs aba={aba} setAba={setAba} tabs={TABS} />
      {aba === 'overview'    && <AbaVisaoGeral />}
      {aba === 'integracoes' && <AbaIntegracoes onSync={verificarAlertasVerba} isAdmin={isAdmin} />}
      {aba === 'demandas'    && <AbaDemandas clientes={clientes} usuarios={usuarios} onRefresh={carregarTudo} />}
      {aba === 'reunioes'    && <AbaReunioes clientes={clientes} usuarios={usuarios} onRefresh={carregarTudo} />}
    </div>
  );
}
